import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas } from '../lib/rutas';
import {
  Users,
  Search,
  Circle,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
  ChevronRight,
  Activity,
  Sparkles,
} from 'lucide-react';

interface TeamMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: 'seller' | 'motoboy' | 'manager';
  ruta: number;
  assigned_ruta: number | null;
  is_coringa: boolean;
  last_seen_at: string | null;
  last_session_id: string | null;
  last_device_type: string | null;
  last_os: string | null;
  last_browser: string | null;
  last_city: string | null;
  last_region: string | null;
  seconds_today: number;
  seconds_week: number;
  seconds_month: number;
}

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

function isOnline(lastSeenAt: string | null): boolean {
  if (!lastSeenAt) return false;
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  return diff < ONLINE_THRESHOLD_MS;
}

function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 60) return '0min';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours === 0) return `${minutes}min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Nunca acessou';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'agora mesmo';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

function DeviceIcon({ type }: { type: string | null }) {
  if (type === 'mobile') return <Smartphone className="w-3.5 h-3.5" />;
  if (type === 'tablet') return <Tablet className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
}

const roleLabel: Record<string, string> = {
  seller: 'Vendedor',
  motoboy: 'Motoboy',
  manager: 'Gestor',
};

export default function SellersActivity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [rutaFilter, setRutaFilter] = useState<'all' | number>('all');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'status' | 'name' | 'ruta' | 'today'>('status');

  const isAllowed = user?.role === 'manager' || user?.role === 'admin';

  const load = async (soft = false) => {
    if (!user || !isAllowed) return;
    if (!soft) setLoading(true); else setRefreshing(true);
    try {
      const targetUnit = user.role === 'admin' ? user.unit_id : user.unit_id;
      const { data, error: rpcError } = await supabase.rpc('get_unit_team_activity', {
        target_unit_id: targetUnit,
      });
      if (rpcError) throw rpcError;
      setMembers((data as TeamMember[]) || []);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Não foi possível carregar a lista');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    const interval = setInterval(() => load(true), 60_000);
    return () => clearInterval(interval);
  }, [user?.id, user?.unit_id]);

  const availableRutas = useMemo(() => {
    const visible = getVisibleRutas(user);
    const set = new Set<number>();
    members.forEach((m) => {
      if (m.role !== 'manager') set.add(m.assigned_ruta ?? m.ruta);
    });
    return Array.from(set).filter((r) => visible.includes(r)).sort((a, b) => a - b);
  }, [members, user]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members
      .filter((m) => (m.role === 'seller' || m.role === 'motoboy'))
      .filter((m) => {
        if (term) {
          const hay = `${m.display_name || ''} ${m.email}`.toLowerCase();
          if (!hay.includes(term)) return false;
        }
        if (rutaFilter !== 'all') {
          const r = m.assigned_ruta ?? m.ruta;
          if (r !== rutaFilter) return false;
        }
        if (onlineOnly && !isOnline(m.last_seen_at)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'status') {
          const onA = isOnline(a.last_seen_at) ? 0 : 1;
          const onB = isOnline(b.last_seen_at) ? 0 : 1;
          if (onA !== onB) return onA - onB;
          return (b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0)
            - (a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0);
        }
        if (sortBy === 'name') {
          const na = (a.display_name || a.email).toLowerCase();
          const nb = (b.display_name || b.email).toLowerCase();
          return na.localeCompare(nb);
        }
        if (sortBy === 'ruta') {
          return (a.assigned_ruta ?? a.ruta) - (b.assigned_ruta ?? b.ruta);
        }
        return b.seconds_today - a.seconds_today;
      });
  }, [members, search, rutaFilter, onlineOnly, sortBy]);

  const onlineCount = useMemo(
    () => filtered.filter((m) => isOnline(m.last_seen_at)).length,
    [filtered]
  );

  if (!isAllowed) {
    return (
      <div className="p-8 text-center text-gray-500">
        Área restrita à gestão da unidade.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">Atividade da equipe</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Acompanhe quem está online agora, o tempo total conectado no dia, na semana e no mês, e clique em um vendedor para ver o histórico completo dos últimos 60 dias.
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Equipe visível</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{filtered.length}</span>
            <span className="text-xs text-gray-500">vendedores e motoboys</span>
          </div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Online agora</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-emerald-700">{onlineCount}</span>
            <span className="text-xs text-emerald-700/70">nos últimos 5 min</span>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Rutas ativas</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{availableRutas.length}</span>
            <span className="text-xs text-gray-500">com equipe atribuída</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={rutaFilter === 'all' ? 'all' : String(rutaFilter)}
              onChange={(e) => setRutaFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">Todas as rutas</option>
              {availableRutas.map((r) => (
                <option key={r} value={r}>Ruta {r}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="status">Ordenar por status</option>
              <option value="today">Mais tempo hoje</option>
              <option value="name">Nome</option>
              <option value="ruta">Ruta</option>
            </select>
            <button
              onClick={() => setOnlineOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                onlineOnly
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Circle className={`w-3 h-3 ${onlineOnly ? 'fill-emerald-500 text-emerald-500' : 'text-gray-400'}`} />
              Somente online
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-sm text-gray-500 py-12 border border-dashed border-gray-200 rounded-xl">
            <Users className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            Nenhum vendedor encontrado com os filtros atuais.
          </div>
        )}
        {filtered.map((m) => {
          const online = isOnline(m.last_seen_at);
          const rutaLabel = m.assigned_ruta ?? m.ruta;
          const name = m.display_name || m.email.split('@')[0];
          return (
            <button
              key={m.user_id}
              onClick={() => navigate(`/dashboard/team-activity/${m.user_id}`)}
              className="group text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        online
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                        }`}
                      />
                      {online ? 'Online' : 'Offline'}
                    </span>
                    {m.is_coringa && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        <Sparkles className="w-2.5 h-2.5" />
                        Coringa
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 font-semibold text-gray-900 truncate">{name}</div>
                  <div className="text-xs text-gray-500 truncate">{m.email}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                  {roleLabel[m.role]}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                  Ruta {rutaLabel}
                </span>
                <span className="inline-flex items-center gap-1 text-gray-500">
                  <DeviceIcon type={m.last_device_type} />
                  {m.last_device_type === 'mobile'
                    ? 'Celular'
                    : m.last_device_type === 'tablet'
                    ? 'Tablet'
                    : 'Computador'}
                  {m.last_browser ? ` · ${m.last_browser}` : ''}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Hoje</div>
                  <div className="text-sm font-semibold text-gray-800">{formatDuration(m.seconds_today)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Semana</div>
                  <div className="text-sm font-semibold text-gray-800">{formatDuration(m.seconds_week)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Mês</div>
                  <div className="text-sm font-semibold text-gray-800">{formatDuration(m.seconds_month)}</div>
                </div>
              </div>

              <div className="mt-3 text-[11px] text-gray-500 flex items-center justify-between">
                <span>
                  {online ? 'Ativo agora' : `Visto ${formatRelative(m.last_seen_at)}`}
                </span>
                {(m.last_city || m.last_region) && (
                  <span className="truncate ml-2 text-gray-400">
                    {[m.last_city, m.last_region].filter(Boolean).join(' / ')}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-gray-400 pt-2">
        A localização é aproximada, obtida pelo endereço de internet do dispositivo, e não corresponde à posição de GPS. Registros com mais de 60 dias são removidos automaticamente.
      </p>
    </div>
  );
}
